import { useMemo, useState } from "react";
import { Modal } from "./Modal";
import { Money, Tag } from "./primitives";
import { fmtMicro, shortAddr, symbolFor } from "./mkt-format";
import { useArtworks, useListings, useMarketplaceConfig } from "./useMarketplace";
import { goMarketplace } from "./mkt-routing";
import { useT } from "../../i18n";

interface Hit {
  id: string;
  kind: "land" | "artwork";
  title: string;
  subtitle: string;
  price?: number;
  currency?: string;
  open: () => void;
}

export function MarketplaceSearch() {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="marketplace-search-btn"
        aria-label={t("marketplace.nav.searchAria")}
        onClick={() => setOpen(true)}
        className="h-9 px-3 inline-flex items-center gap-2 rounded-md text-text-secondary hover:text-neon-teal hover:bg-elevated/60 transition-colors font-mono text-[10.5px] tracking-[0.14em] uppercase"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <circle cx="6" cy="6" r="4.2" stroke="currentColor" strokeWidth="1.3" />
          <path d="M9.2 9.2L12 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        <span className="hidden sm:inline">{t("marketplace.nav.search")}</span>
      </button>
      {open && <SearchModal onClose={() => setOpen(false)} />}
    </>
  );
}

function SearchModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [q, setQ] = useState("");
  const { data: config } = useMarketplaceConfig();
  const listingsQuery = useListings("active");
  const artworksQuery = useArtworks();

  const hits = useMemo<Hit[]>(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const out: Hit[] = [];
    for (const l of listingsQuery.data?.listings ?? []) {
      const hay = `parcel #${l.token_id} ${l.token_id} ${l.seller} ${l.parcel_id ?? ""}`.toLowerCase();
      if (hay.includes(term)) {
        out.push({
          id: l.order_hash, kind: "land", title: t("marketplace.common.parcelNumber", { id: String(l.token_id) }),
          subtitle: t("marketplace.search.sellerPrefix", { addr: shortAddr(l.seller) }), price: l.price_micro, currency: l.currency,
          open: () => goMarketplace("land", {}, { kind: "land", id: l.order_hash }),
        });
      }
    }
    for (const a of artworksQuery.data?.artworks ?? []) {
      const hay = `${a.title} ${a.creator} ${a.owner} ${a.artwork_id}`.toLowerCase();
      if (hay.includes(term)) {
        out.push({
          id: a.artwork_id, kind: "artwork", title: a.title,
          subtitle: `${t("marketplace.search.byPrefix", { addr: shortAddr(a.creator) })}${a.auction ? t("marketplace.search.auctionSuffix") : ""}`,
          price: a.auction?.reserve_micro, currency: a.auction?.currency,
          open: () => goMarketplace("artworks", {}, { kind: "artwork", id: a.artwork_id }),
        });
      }
    }
    return out.slice(0, 20);
  }, [q, listingsQuery.data, artworksQuery.data, t]);

  return (
    <Modal open onClose={onClose} title={t("marketplace.search.title")} subtitle={t("marketplace.search.subtitle")} testId="search-modal" size="lg">
      <input
        autoFocus
        data-testid="search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t("marketplace.search.placeholder")}
        className="w-full h-11 px-3 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[13px] focus:outline-none focus:border-neon-teal/60"
      />
      <div className="mt-4 min-h-[120px]">
        {q.trim() === "" ? (
          <p className="py-8 text-center font-mono text-[11px] text-text-muted">{t("marketplace.search.startTyping")}</p>
        ) : hits.length === 0 ? (
          <p data-testid="search-empty" className="py-8 text-center font-mono text-[11px] text-text-muted">{t("marketplace.search.noMatches", { q })}</p>
        ) : (
          <div data-testid="search-results" className="divide-y divide-border-divider rounded-lg border border-border-divider overflow-hidden">
            {hits.map((h) => (
              <button key={`${h.kind}-${h.id}`} type="button" data-testid="search-result-row"
                onClick={() => { h.open(); onClose(); }}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-elevated/60 transition-colors text-left">
                <div className="min-w-0">
                  <span className="font-display text-sm text-text-primary truncate block">{h.title}</span>
                  <span className="font-mono text-[10px] text-text-muted">{h.subtitle}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Tag tone={h.kind === "artwork" ? "coral" : "teal"}>{h.kind === "artwork" ? t("marketplace.search.artwork") : t("marketplace.search.land")}</Tag>
                  {h.price ? <Money amount={fmtMicro(h.price)} symbol={symbolFor(h.currency, config)} className="text-sm text-neon-teal" /> : null}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
